import './styles/App.css';
import './styles/theme.css';
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { loginWithSSOToken } from './api/authService';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const Login          = React.lazy(() => import('./pages/Login'));
const Dashboard      = React.lazy(() => import('./pages/Dashboard'));
const Organisations  = React.lazy(() => import('./pages/Organisations'));
const Organisation      = React.lazy(() => import('./pages/Organisation'));
const UnassignedUsers   = React.lazy(() => import('./pages/UnassignedUsers'));
const TalentPool     = React.lazy(() => import('./pages/TalentPool'));
const JobPostings    = React.lazy(() => import('./pages/JobPostings'));
const CreateJob      = React.lazy(() => import('./pages/CreateJob'));
const EditJob        = React.lazy(() => import('./pages/EditJob'));
const ManageSponsors  = React.lazy(() => import('./pages/ManageSponsors'));
const SponsorDetail   = React.lazy(() => import('./pages/SponsorDetail'));
const AboutCompany    = React.lazy(() => import('./pages/AboutCompany'));
const Settings        = React.lazy(() => import('./pages/Settings'));
const ForgotPassword  = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword   = React.lazy(() => import('./pages/ResetPassword'));
const NotFound        = React.lazy(() => import('./pages/NotFound'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-white">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-theme-primary mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium text-sm">Loading...</p>
    </div>
  </div>
);

// Handles ?sso_token= coming from control centre
function SSOHandler() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');

    if (ssoToken && !isAuthenticated) {
      try {
        const result = loginWithSSOToken(ssoToken);
        login(result);
        // Remove token from URL so it doesn't stay in browser history
        params.delete('sso_token');
        const cleanUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', cleanUrl);
        navigate('/dashboard', { replace: true });
      } catch {
        navigate('/login', { replace: true });
      }
    }
  }, []);

  return null;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated === null) return <LoadingSpinner />;

  return (
    <>
      <SSOHandler />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/login"
            element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/password-reset/:userId/:token" element={<ResetPassword />} />
          <Route path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          {/* Super admin only */}
          <Route path="/organisations"
            element={<ProtectedRoute superOnly><Organisations /></ProtectedRoute>}
          />
          <Route path="/organisations/unassigned"
            element={<ProtectedRoute superOnly><UnassignedUsers /></ProtectedRoute>}
          />
          <Route path="/organisations/:orgId"
            element={<ProtectedRoute superOnly><Organisation /></ProtectedRoute>}
          />
          {/* Club admin only — manage their org's sponsors */}
          <Route path="/manage-sponsors"
            element={<ProtectedRoute adminOnly><ManageSponsors /></ProtectedRoute>}
          />
          <Route path="/manage-sponsors/:id"
            element={<ProtectedRoute adminOnly><SponsorDetail /></ProtectedRoute>}
          />
          {/* Sponsor only */}
          <Route path="/about-company"
            element={<ProtectedRoute><AboutCompany /></ProtectedRoute>}
          />
          {/* All authenticated users */}
          <Route path="/talent-pool"
            element={<ProtectedRoute><TalentPool /></ProtectedRoute>}
          />
          <Route path="/job-postings"
            element={<ProtectedRoute><JobPostings /></ProtectedRoute>}
          />
          <Route path="/job-postings/new"
            element={<ProtectedRoute><CreateJob /></ProtectedRoute>}
          />
          <Route path="/job-postings/:id/edit"
            element={<ProtectedRoute><EditJob /></ProtectedRoute>}
          />
          <Route path="/settings"
            element={<ProtectedRoute><Settings /></ProtectedRoute>}
          />
          <Route path="/"
            element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
          />
          <Route path="*"
            element={isAuthenticated ? <NotFound /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
